import { useState, useEffect } from 'react';
import { ArrowLeft, Loader2, ShieldAlert } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from '@/hooks/use-toast';
import type { ProductCatalogItem, ProductCatalogRequest } from '@/types/productCatalog';

interface ProductFormProps {
  initialData?: ProductCatalogItem;
  onSave: (data: ProductCatalogRequest) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

interface ProductFormState {
  title: string;
  price: string;
  quantity: string;
  category: string;
  is_fragile: boolean;
}

const EMPTY_FORM: ProductFormState = {
  title: '',
  price: '',
  quantity: '1',
  category: '',
  is_fragile: false,
};

const ProductForm = ({ initialData, onSave, onCancel, isLoading = false }: ProductFormProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [form, setForm] = useState<ProductFormState>(EMPTY_FORM);
  const [fieldErrors, setFieldErrors] = useState<Record<string, boolean>>({});

  const isEditMode = !!initialData;

  // Prefill from existing data
  useEffect(() => {
    if (initialData) {
      setForm({
        title: initialData.title ?? '',
        price: String(initialData.price ?? ''),
        quantity: String(initialData.quantity ?? 1),
        category: initialData.category ?? '',
        is_fragile: initialData.is_fragile ?? false,
      });
    }
  }, [initialData]);

  const inputClass = (field?: string) =>
    `w-full h-10 ps-3 pe-3 rounded-lg border text-sm text-sd-foreground focus:outline-none focus:ring-2 focus:ring-sd-ring ${
      field && fieldErrors[field]
        ? 'border-sd-destructive bg-sd-destructive/5'
        : 'border-sd-input bg-sd-input-bg'
    }`;

  const handleSave = () => {
    const errors: Record<string, boolean> = {};

    if (!form.title.trim()) errors.title = true;

    // Price is optional — only validate format when provided
    const priceNum = form.price !== '' ? parseFloat(form.price) : null;
    if (priceNum !== null && (isNaN(priceNum) || priceNum < 0)) errors.price = true;

    setFieldErrors(errors);

    if (Object.keys(errors).length > 0) {
      const missing = Object.keys(errors)
        .map((k) => String(t(k, { defaultValue: k })))
        .join(', ');
      toast({
        title: String(t('required_fields_missing', { defaultValue: 'Required fields missing' })),
        description: missing,
        variant: 'destructive',
      });
      return;
    }

    const quantityNum = parseInt(form.quantity, 10);

    const payload: ProductCatalogRequest = {
      title: form.title.trim(),
      price: priceNum,
      quantity: isNaN(quantityNum) || quantityNum < 1 ? 1 : quantityNum,
      category: form.category.trim() || null,
      is_fragile: form.is_fragile,
    };

    onSave(payload);
  };

  return (
    <div className='sd-scope sd-font flex flex-col'>
      <div className='p-6 space-y-6 max-w-3xl w-full mx-auto'>
        <div className='rounded-2xl border border-sd-border bg-sd-card shadow-sm'>
          {/* Header */}
          <div className='px-6 py-4 border-b border-sd-border flex items-center gap-3'>
            <button
              onClick={onCancel}
              className='text-sd-muted-foreground hover:text-sd-foreground transition-colors'
            >
              <ArrowLeft className='h-4 w-4' />
            </button>
            <h2 className='text-sm font-semibold text-sd-foreground m-0'>
              {isEditMode
                ? String(t('edit_product', { defaultValue: 'Edit Product' }))
                : String(t('add_product', { defaultValue: 'Add Product' }))}
            </h2>
          </div>

          <div className='p-6 space-y-6'>
            {/* Section: Product Details */}
            <div>
              <p className='text-xs font-semibold text-sd-muted-foreground uppercase tracking-wider mb-3'>
                {String(t('product_details', { defaultValue: 'Product Details' }))}
              </p>
              <div className='space-y-4'>
                {/* Title */}
                <div className='space-y-1.5'>
                  <label className='text-xs font-medium text-sd-muted-foreground'>
                    {String(t('product_title', { defaultValue: 'Product Title' }))} *
                  </label>
                  <input
                    type='text'
                    value={form.title}
                    onChange={(e) => {
                      setForm((f) => ({ ...f, title: e.target.value }));
                      setFieldErrors((prev) => ({ ...prev, title: false }));
                    }}
                    placeholder={String(t('product_title_placeholder', { defaultValue: 'e.g. Blue T-Shirt' }))}
                    className={inputClass('title')}
                  />
                  {fieldErrors.title && (
                    <p className='text-xs text-sd-destructive mt-1'>
                      {String(t('this_field_is_required', { defaultValue: 'This field is required' }))}
                    </p>
                  )}
                </div>

                {/* Price */}
                <div className='space-y-1.5'>
                  <label className='text-xs font-medium text-sd-muted-foreground'>
                    {String(t('price', { defaultValue: 'Price' }))}
                  </label>
                  <div className='relative'>
                    <span className='absolute start-3 top-1/2 -translate-y-1/2 text-xs font-medium text-sd-muted-foreground pointer-events-none select-none'>
                      IQD
                    </span>
                    <input
                      type='number'
                      min='0'
                      step='0.01'
                      value={form.price}
                      onChange={(e) => {
                        setForm((f) => ({ ...f, price: e.target.value }));
                        setFieldErrors((prev) => ({ ...prev, price: false }));
                      }}
                      placeholder='0'
                      className={`w-full h-10 ps-12 pe-3 rounded-lg border text-sm text-sd-foreground focus:outline-none focus:ring-2 focus:ring-sd-ring ${
                        fieldErrors.price
                          ? 'border-sd-destructive bg-sd-destructive/5'
                          : 'border-sd-input bg-sd-input-bg'
                      }`}
                    />
                  </div>
                  {fieldErrors.price && (
                    <p className='text-xs text-sd-destructive mt-1'>
                      {String(t('price_must_be_valid', { defaultValue: 'Enter a valid price (0 or above)' }))}
                    </p>
                  )}
                </div>

                {/* Quantity hidden — always defaults to 1. Uncomment to re-enable. */}

                {/* Category */}
                <div className='space-y-1.5'>
                  <label className='text-xs font-medium text-sd-muted-foreground'>
                    {String(t('category', { defaultValue: 'Category' }))}
                  </label>
                  <input
                    type='text'
                    value={form.category}
                    onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                    placeholder={String(
                      t('category_placeholder', { defaultValue: 'e.g. Clothing, Electronics' }),
                    )}
                    className={inputClass()}
                  />
                </div>

                {/* Fragile toggle */}
                <label className='flex items-center gap-3 cursor-pointer select-none py-1'>
                  <div
                    className={`relative w-9 h-5 rounded-full transition-colors ${
                      form.is_fragile ? 'bg-[rgb(237,224,74)]' : 'bg-sd-muted'
                    }`}
                    onClick={() => setForm((f) => ({ ...f, is_fragile: !f.is_fragile }))}
                  >
                    <div
                      className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow-sm transition-transform ${
                        form.is_fragile ? 'translate-x-[18px]' : 'translate-x-0.5'
                      }`}
                    />
                  </div>
                  <div className='flex items-center gap-1.5'>
                    <ShieldAlert className='h-3.5 w-3.5 text-sd-muted-foreground' />
                    <span className='text-sm text-sd-foreground'>
                      {String(t('fragile_product', { defaultValue: 'Fragile product' }))}
                    </span>
                  </div>
                  <span className='text-xs text-sd-muted-foreground'>
                    {String(t('fragile_product_hint', { defaultValue: 'Orders with this product will be marked as fragile' }))}
                  </span>
                </label>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className='px-6 py-4 border-t border-sd-border flex justify-end gap-3'>
            <button
              onClick={onCancel}
              className='px-4 py-2.5 rounded-lg border border-sd-input bg-sd-card text-sm font-medium hover:bg-sd-muted transition-colors'
            >
              {String(t('cancel', { defaultValue: 'Cancel' }))}
            </button>
            <button
              onClick={handleSave}
              disabled={isLoading}
              className='flex items-center gap-2 px-6 py-2.5 rounded-lg bg-sd-primary text-sd-primary-foreground text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-60'
            >
              {isLoading && <Loader2 className='h-4 w-4 animate-spin' />}
              {isLoading
                ? String(t('saving', { defaultValue: 'Saving...' }))
                : isEditMode
                  ? String(t('update_product', { defaultValue: 'Update Product' }))
                  : String(t('save_product', { defaultValue: 'Save Product' }))}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductForm;
